/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { Children, isValidElement } from 'react';
import { StyleSheet, Text, Link, View } from '@react-pdf/renderer';
import ReactMarkdown, { defaultUrlTransform, type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { PDF_COLORS, PDF_FONT_FAMILY } from './styles';
import rehypeUnwrapImages from 'rehype-unwrap-images';
import { joinApiPath } from '@/lib/apiPath';
import { HOST } from '@/lib/fetch';
import { MERCURY_URL } from '@/constants/env';
import { RemoteImage } from '@/client/features/issue/components/RemoteImage';

const PdfImage = ({ src }: { src?: string }) => <RemoteImage src={src} />;

const styles = StyleSheet.create({
  text: {
    fontSize: 12,
    textAlign: 'justify',
    fontFamily: PDF_FONT_FAMILY,
    fontWeight: 400,
    color: PDF_COLORS.textPrimary,
    lineHeight: 1.4,
  },
  caption: {
    fontSize: 10,
    fontFamily: PDF_FONT_FAMILY,
    fontWeight: 400,
    color: PDF_COLORS.textPrimary,
    lineHeight: 1.3,
    marginTop: 2,
  },

  strong: {
    fontFamily: PDF_FONT_FAMILY,
    fontWeight: 700,
  },
  em: {
    fontStyle: 'italic',
  },
  del: {
    textDecoration: 'line-through',
  },
  code: {
    fontFamily: 'Courier',
    fontSize: 10,
    backgroundColor: '#f8f9fa',
  },
  link: {
    fontFamily: PDF_FONT_FAMILY,
    color: PDF_COLORS.link,
  },
  h1: {
    fontFamily: PDF_FONT_FAMILY,
    fontSize: 16,
    marginTop: 12,
    fontWeight: 700,
    color: PDF_COLORS.textPrimary,
  },
  h2: {
    fontFamily: PDF_FONT_FAMILY,
    fontSize: 14,
    marginTop: 10,
    fontWeight: 500,
    color: PDF_COLORS.textPrimary,
  },
  h3: {
    fontFamily: PDF_FONT_FAMILY,
    fontSize: 13,
    marginTop: 10,
    fontWeight: 500,
    color: PDF_COLORS.textPrimary,
  },
  h4: {
    fontFamily: PDF_FONT_FAMILY,
    fontSize: 12,
    marginTop: 10,
    fontWeight: 700,
    color: PDF_COLORS.textPrimary,
  },
  h5: {
    fontFamily: PDF_FONT_FAMILY,
    fontSize: 11,
    marginTop: 10,
    fontWeight: 700,
    color: PDF_COLORS.textPrimary,
  },
  h6: {
    fontFamily: PDF_FONT_FAMILY,
    fontSize: 10,
    marginTop: 10,
    fontWeight: 700,
    textDecoration: 'underline',
    color: PDF_COLORS.textPrimary,
  },
  blockquote: {
    borderLeft: '2px solid #dee2e6',
    paddingLeft: 6,
    marginVertical: 6,
    lineHeight: 1.4,
  },
  pre: {
    borderRadius: 4,
    marginTop: 10,
    borderLeft: `2px solid ${PDF_COLORS.brandAccent}`,
    whiteSpace: 'pre-wrap',
    unicodeBidi: 'isolate',
    fontSize: 10,
    fontFamily: 'Courier',
    backgroundColor: '#f8f9fa',
    padding: 10,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: 6,
    marginBottom: 8,
  },
  listItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    fontFamily: PDF_FONT_FAMILY,
    fontWeight: 400,
    fontSize: 12,
    marginTop: 4,
    lineHeight: 1.4,
    color: PDF_COLORS.textPrimary,
  },
  // Squared orange bullet (per Mercury design guide).
  bulletSquare: {
    width: 4,
    height: 4,
    backgroundColor: PDF_COLORS.bulletSquare,
    marginTop: 6,
    marginRight: 8,
  },
  listIndex: {
    fontFamily: PDF_FONT_FAMILY,
    fontWeight: 500,
    color: PDF_COLORS.bulletSquare,
    marginRight: 6,
  },
  listContent: {
    flex: 1,
    textAlign: 'justify',
  },
  hr: {
    borderTop: '1px solid #dee2e6',
    marginTop: 6,
  },
  footnote: {
    fontSize: 10,
  },
  footnoteWrapper: {
    borderTop: '1px solid #dfe3e7',
    marginTop: 6,
  },

  table: {
    width: 'auto',
    textAlign: 'left',
  },
  thead: {},
  th: {
    flex: 1,
    padding: 4,
    fontFamily: PDF_FONT_FAMILY,
    fontSize: 10,
    fontWeight: 700,
    color: PDF_COLORS.textPrimary,
  },
  tbody: {},
  tr: {
    flexDirection: 'row',
    marginVertical: 2.5,
    borderBottom: '0.5px solid #d8dbe8',
  },
  td: {
    flex: 1,
    padding: 4,
    fontFamily: PDF_FONT_FAMILY,
    fontSize: 10,
    color: PDF_COLORS.textPrimary,
  },
});

const removeSpecialChars = (footnote: string) => footnote.replace(/[^a-zA-Z0-9]/g, '');

const wrapText = (children: React.ReactNode): React.ReactNode => {
  if (children === '\n') {
    return null;
  }
  if (typeof children === 'string') {
    return <Text orphans={3}>{children}</Text>;
  }
  if (Array.isArray(children)) {
    return Children.map(children, (child: React.ReactNode) => wrapText(child));
  }
  return children;
};

const BLOCK_TAGS = new Set(['ul', 'ol', 'blockquote', 'pre', 'table', 'img', 'section']);

const components = (footnoteClass: string): Components => ({
  p: ({ children, node }) => {
    const hasImage = node?.children?.some(
      (child: any) => child.type === 'element' && child.tagName === 'img'
    );

    if (!hasImage) {
      return (
        <Text style={styles.text} orphans={3}>
          {children}
        </Text>
      );
    }

    const groups: React.ReactNode[] = [];
    let inlineGroup: React.ReactNode[] = [];

    const flushInline = () => {
      if (inlineGroup.length > 0) {
        groups.push(
          <Text key={`img-p-text-${groups.length}`} style={styles.caption}>
            {inlineGroup}
          </Text>
        );
        inlineGroup = [];
      }
    };

    Children.forEach(children, (child: React.ReactNode) => {
      if (child === '\n') return;
      if (isValidElement(child) && child.type === PdfImage) {
        flushInline();
        groups.push(child);
      } else if (typeof child === 'string') {
        if (child.trim()) inlineGroup.push(child);
      } else {
        inlineGroup.push(child);
      }
    });
    flushInline();

    return <View wrap={false}>{groups}</View>;
  },
  em: ({ children }) => <Text style={styles.em}>{children}</Text>,
  i: ({ children }) => <Text style={styles.em}>{children}</Text>,
  strong: ({ children }) => <Text style={styles.strong}>{children}</Text>,
  del: ({ children }) => <Text style={styles.del}>{children}</Text>,
  b: ({ children }) => <Text style={styles.strong}>{children}</Text>,
  code: ({ children }) => <Text style={styles.code}>{children}</Text>,
  a: ({ children, id, node, href }) => {
    // Handle footnote links
    if (node?.properties?.dataFootnoteRef) {
      const footnoteHref = `#${removeSpecialChars(`${footnoteClass}-${href?.split('#')[1]}`)}`;
      const selector = removeSpecialChars(`${footnoteClass}-${id}`);
      return (
        <Link style={styles.link} src={footnoteHref || ''} id={selector}>
          {children}
        </Link>
      );
    }

    return (
      <Link style={styles.link} src={href || ''} id={id}>
        {children}
      </Link>
    );
  },
  br: () => <Text>{'\n'}</Text>,
  h1: ({ children }) => (
    <Text style={styles.h1} minPresenceAhead={80}>
      {children}
    </Text>
  ),
  h2: ({ children }) => (
    <Text style={styles.h2} minPresenceAhead={60}>
      {children}
    </Text>
  ),
  h3: ({ children }) => (
    <Text style={styles.h3} minPresenceAhead={50}>
      {children}
    </Text>
  ),
  h4: ({ children }) => (
    <Text style={styles.h4} minPresenceAhead={50}>
      {children}
    </Text>
  ),
  h5: ({ children }) => (
    <Text style={styles.h5} minPresenceAhead={50}>
      {children}
    </Text>
  ),
  h6: ({ children }) => (
    <Text style={styles.h6} minPresenceAhead={50}>
      {children}
    </Text>
  ),
  blockquote: ({ children }) => (
    <View style={styles.blockquote} wrap={false}>
      {wrapText(children)}
    </View>
  ),
  pre: ({ children }) => (
    <View style={styles.pre} wrap={false}>
      {wrapText(children)}
    </View>
  ),
  img: PdfImage,
  hr: () => <Text style={styles.hr} />,

  ul: ({ children }) => (
    <View style={styles.list}>
      {Children.map(children, (node: React.ReactNode) => {
        if (isValidElement(node)) {
          return (
            <View key={node.key} style={styles.listItem} wrap={false}>
              <View style={styles.bulletSquare} />
              {wrapText(node)}
            </View>
          );
        }
        return null;
      })}
    </View>
  ),
  ol: ({ children }) => {
    let index = 0;
    return (
      <View style={styles.list}>
        {Children.map(children, (node: React.ReactNode) => {
          if (isValidElement(node)) {
            index += 1;
            return (
              <View key={node.key} style={styles.listItem} wrap={false}>
                <Text style={styles.listIndex}>{`${index}.`}</Text>
                {wrapText(node)}
              </View>
            );
          }
          return null;
        })}
      </View>
    );
  },
  li: ({ children, id, node }) => {
    const hasBlockChild = node?.children?.some(
      (child: any) => child.type === 'element' && BLOCK_TAGS.has(child.tagName)
    );
    const filteredChildren = Children.toArray(children).filter((c) => c !== '\n');

    if (id?.includes('user-content')) {
      const selector = removeSpecialChars(`${footnoteClass}-${id}`);
      if (hasBlockChild) {
        return (
          <View id={selector} style={[styles.listContent, styles.footnote]}>
            {wrapText(children)}
          </View>
        );
      }
      return (
        <Text id={selector} style={[styles.listContent, styles.footnote]}>
          {filteredChildren}
        </Text>
      );
    }

    if (hasBlockChild) {
      return <View style={styles.listContent}>{wrapText(children)}</View>;
    }
    return <Text style={styles.listContent}>{filteredChildren}</Text>;
  },

  table: ({ children }) => <View style={styles.table}>{children}</View>,
  thead: ({ children }) => (
    <View fixed break style={styles.thead}>
      {children}
    </View>
  ),
  th: ({ children }) => (
    <View style={styles.th}>
      <Text>{children}</Text>
    </View>
  ),
  tbody: ({ children }) => <View>{children}</View>,
  tr: ({ children }) => <View style={styles.tr}>{children}</View>,

  td: ({ children }) => (
    <View style={styles.td}>
      <Text>{children}</Text>
    </View>
  ),

  section: ({ children }) => <View style={styles.footnoteWrapper}>{wrapText(children)}</View>,
});

interface PDFMarkdownProps {
  text: string;
  footnoteClass: string;
  issue: number;
  resource: string;
}

const PDFMarkdown = ({ text, footnoteClass, resource, issue }: PDFMarkdownProps) => {
  const attachmentPath = `v0${joinApiPath(resource, String(issue), 'attachments', 'file')}`;

  return (
    <ReactMarkdown
      components={components(footnoteClass)}
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeUnwrapImages]}
      urlTransform={(src, key) => {
        // Local images are considered to be attachments
        if (key === 'src' && !src.includes('/')) {
          // An empty destination is not an attachment name, so keep it empty.
          return src.trim() ? `${MERCURY_URL}${HOST}/${attachmentPath}/${src}` : '';
        }

        return defaultUrlTransform(src);
      }}
    >
      {text?.normalize('NFKC')}
    </ReactMarkdown>
  );
};

export default PDFMarkdown;
